import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Layout, LayoutContent, LayoutFooter, LayoutPanel } from '@astryxdesign/core/Layout'
import { Stack, StackItem } from '@astryxdesign/core/Stack'
import { Center } from '@astryxdesign/core/Center'
import { Heading } from '@astryxdesign/core/Heading'
import { Text } from '@astryxdesign/core/Text'
import { Button } from '@astryxdesign/core/Button'
import { IconButton } from '@astryxdesign/core/IconButton'
import { Icon } from '@astryxdesign/core/Icon'
import { Banner } from '@astryxdesign/core/Banner'
import { Spinner } from '@astryxdesign/core/Spinner'
import { StatusDot } from '@astryxdesign/core/StatusDot'
import { Token } from '@astryxdesign/core/Token'
import { Toolbar } from '@astryxdesign/core/Toolbar'
import { EmptyState } from '@astryxdesign/core/EmptyState'
import { DateInput } from '@astryxdesign/core/DateInput'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { RadioList, RadioListItem } from '@astryxdesign/core/RadioList'
import { TextInput } from '@astryxdesign/core/TextInput'
import { CheckboxInput } from '@astryxdesign/core/CheckboxInput'
import { Table, TableHeader, TableBody, TableRow, TableCell, TableHeaderCell } from '@astryxdesign/core/Table'
import CourtBookingPanel from './CourtBookingPanel'
import CreateGameDialog from './CreateGameDialog'
import AssigneePicker from './AssigneePicker'
import { EyeIcon } from './consoleIcons'
import { readCourtDay, blockCourtSlots } from './consoleApi'
import {
    BLOCK_KINDS,
    SHOW_REASON_LABEL,
    addDays,
    blockResultText,
    courtColumn,
    courtTitle,
    dayRows,
    dayTitle,
    failureText,
    halfHourLook,
    isoDay,
    lostText,
    normalizeCourtDay,
    parseIsoDay,
    sameDay,
    selectionText,
    showReasonDescription,
    slotRangeText,
    slotTime,
    todayParts,
} from './consoleText'

// The day view: every court of the club side by side, one row per half-hour
// (lunar-falcon-2), and what holds each one - a hold is one cell over its
// half-hours. Free half-hours are picked to be blocked for an event, a
// tournament or maintenance, each as its own 30 minutes; the start of each
// player's slot is ruled and timed, and free half-hours no slot covers are
// hatched as lost. A taken one opens its booking in the
// side panel, where a game can be cancelled and a block lifted. A day the
// club is closed has no grid at all, only the way to its opening hours.
//
// The grid is a Table in children mode - courts are its columns - and the
// booking opens in the end panel of a Layout, the master-detail pattern, so
// the grid stays in view while the panel is open.
const LABEL_MAX = 80
const PANEL_WIDTH = 380

// The time column is as narrow as "20:30" allows; the courts share the rest.
const timeColumnStyle = { width: 'calc(var(--spacing-10) * 2)', minWidth: 'calc(var(--spacing-10) * 2)' }
const cellStyles = {
    interactive: { cursor: 'pointer' },
    selected: { cursor: 'pointer', backgroundColor: 'var(--color-accent-muted)' },
    past: { backgroundColor: 'var(--color-background-muted)' },
    open: { cursor: 'pointer', backgroundColor: 'var(--color-overlay-pressed)' },
    // A free half-hour no player can book: hatched, and still blockable.
    lost: {
        cursor: 'pointer',
        backgroundImage: 'repeating-linear-gradient(135deg, var(--color-background-muted) 0 var(--spacing-1), transparent var(--spacing-1) var(--spacing-2))',
    },
    slotStart: { borderTop: 'var(--spacing-0-5) solid var(--color-border-emphasized)' },
}

const cellKey = (courtId, startTime) => `${courtId}\n${startTime}`

export default function CourtDay({ clubId, date, onDateChange, onOpenHours, onSessionLost }) {
    const [load, setLoad] = useState({ status: 'loading', courts: [] })
    const [selection, setSelection] = useState(() => new Map())
    const [notice, setNotice] = useState(null) // { status, title }
    const [openBooking, setOpenBooking] = useState('')
    const [isBlockOpen, setBlockOpen] = useState(false)
    const [isGameOpen, setGameOpen] = useState(false)
    const latest = useRef(0)

    // A failed call: 401 goes back to the login page the way every other
    // console call does, the rest say what happened above the grid.
    const fail = useCallback(status => {
        if (status === 401) {
            onSessionLost()
            return
        }
        setNotice({ status: 'error', title: failureText(status) })
    }, [onSessionLost])

    // Reloads the day. Only the newest answer is kept, so clicking through the
    // days quickly cannot leave an older day on screen.
    const reload = useCallback(async () => {
        const request = ++latest.current
        setLoad(current => ({ ...current, status: current.courts.length > 0 ? 'refreshing' : 'loading' }))
        let answer
        try {
            answer = await readCourtDay(clubId, date)
        } catch (e) {
            answer = { status: 0 }
        }
        if (request !== latest.current) return
        if (answer.status === 200 && answer.json) {
            setLoad({ status: 'ready', courts: normalizeCourtDay(answer.json), isClosed: !!answer.json.is_closed })
        } else if (answer.status === 401) {
            onSessionLost()
        } else {
            setLoad({ status: 'error', courts: [], title: failureText(answer.status) })
        }
    }, [clubId, date, onSessionLost])

    // A new day starts with nothing picked and nothing open.
    const dayKey = isoDay(date)
    useEffect(() => {
        setSelection(new Map())
        setOpenBooking('')
        setNotice(null)
        setLoad({ status: 'loading', courts: [] })
    }, [dayKey, clubId])

    useEffect(() => {
        reload()
    }, [reload])

    const toggle = slot => cell => {
        setSelection(current => {
            const next = new Map(current)
            const key = cellKey(cell.court_id, slot.start_time)
            if (next.has(key)) next.delete(key)
            else next.set(key, { court_id: cell.court_id, start_time: slot.start_time })
            return next
        })
    }

    // After anything that changed the day: say what came of it, drop what was
    // picked or open, and show the day as it is now.
    const changed = useCallback((status, title) => {
        setNotice({ status, title })
        setSelection(new Map())
        setOpenBooking('')
        reload()
    }, [reload])

    // A change that leaves the open booking open - its reason shown or
    // hidden - only says so and redraws the grid behind the panel.
    const updated = useCallback((status, title) => {
        setNotice({ status, title })
        reload()
    }, [reload])

    // A slot somebody else changed between the read and the click: the grid
    // on screen is stale, so it is read again under the message.
    const conflict = useCallback(status => {
        if (status === 409) {
            changed('warning', failureText(status))
            return
        }
        fail(status)
    }, [changed, fail])

    const onBlocked = (blockedCount, skipped) => {
        setBlockOpen(false)
        changed(skipped.length > 0 ? 'warning' : 'success', blockResultText(blockedCount, skipped))
    }

    // A game holds one court for one slot, so it is offered for exactly one
    // picked half-hour, and only where a player's slot starts.
    const picked = selection.size === 1 ? [...selection.values()][0] : null
    const gameCell = picked && startsAPlayerSlot(load.courts, picked) ? picked : null
    const gameWhere = gameCell ? cellWhere(load.courts, gameCell, date) : ''

    const today = todayParts()
    const rows = dayRows(load.courts)
    const columns = load.courts.map(courtColumn)

    const navigation = (
        <Stack direction="horizontal" gap={3} vAlign="center" hAlign="between" wrap="wrap">
            <Stack direction="horizontal" gap={2} vAlign="center" wrap="wrap">
                <IconButton
                    label="Jour précédent"
                    icon={<Icon icon="chevronLeft" />}
                    onClick={() => onDateChange(addDays(date, -1))}
                />
                <Button
                    label="Aujourd'hui"
                    isDisabled={sameDay(date, today)}
                    onClick={() => onDateChange(today)}
                />
                <IconButton
                    label="Jour suivant"
                    icon={<Icon icon="chevronRight" />}
                    onClick={() => onDateChange(addDays(date, 1))}
                />
                <Heading level={2}>{capitalize(dayTitle(date))}</Heading>
                {load.status === 'refreshing' && <Spinner size="sm" label="Mise à jour" />}
            </Stack>
            <DateInput
                label="Aller au"
                isLabelHidden
                value={dayKey}
                weekStartsOn="mon"
                presentation="popover"
                format={value => {
                    const picked = parseIsoDay(value)
                    return picked ? dayTitle(picked) : value
                }}
                width={260}
                onChange={value => {
                    const picked = parseIsoDay(value)
                    if (picked) onDateChange(picked)
                }}
            />
        </Stack>
    )

    const selectionBar = (
        <Toolbar
            label="Sélection"
            size="sm"
            variant={selection.size > 0 ? 'muted' : 'transparent'}
            startContent={selection.size > 0
                ? <Text weight="semibold">{selectionText(selection.size)}</Text>
                : <Text color="secondary" size="sm">Cliquez sur des demi-heures libres pour les bloquer, sur le début d'un créneau libre pour y créer une partie, ou sur un créneau occupé pour en voir le détail.</Text>}
            endContent={selection.size > 0 && (
                <>
                    <Button label="Effacer la sélection" variant="ghost" onClick={() => setSelection(new Map())} />
                    {gameCell && <Button label="Créer une partie…" variant="secondary" onClick={() => setGameOpen(true)} />}
                    <Button label="Bloquer…" variant="primary" onClick={() => setBlockOpen(true)} />
                </>
            )}
        />
    )

    let body
    if (load.status === 'loading') {
        body = (
            <Center axis="both" padding={10}>
                <Spinner label="Chargement des terrains" />
            </Center>
        )
    } else if (load.status === 'error') {
        body = (
            <Stack gap={3} hAlign="start">
                <Banner status="error" title={load.title} />
                <Button label="Réessayer" variant="secondary" onClick={reload} />
            </Stack>
        )
    } else if (load.isClosed) {
        body = (
            <EmptyState
                title="Club fermé ce jour-là"
                description="Modifiez les horaires dans l'onglet Horaires."
                headingLevel={3}
                actions={<Button label="Voir les horaires" variant="secondary" onClick={onOpenHours} />}
            />
        )
    } else if (load.courts.length === 0) {
        body = (
            <EmptyState
                title="Aucun terrain"
                description="Ce club n'a encore aucun terrain dans Padelr."
                headingLevel={3}
            />
        )
    } else {
        body = (
            <Table density="compact" dividers="grid" textOverflow="truncate" verticalAlign="top">
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell style={timeColumnStyle}>Heure</TableHeaderCell>
                        {load.courts.map(court => (
                            <TableHeaderCell key={court.court_id}>
                                <Stack gap={0.5}>
                                    <Text weight="semibold">{courtTitle(court)}</Text>
                                    {court.lost_minutes > 0 && (
                                        <Text size="sm" color="secondary">{lostText(court.lost_minutes)}</Text>
                                    )}
                                </Stack>
                            </TableHeaderCell>
                        ))}
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map(minute => (
                        <TableRow key={minute}>
                            <TableCell style={timeColumnStyle}>
                                <Text hasTabularNumbers weight="medium" color={minute % 60 === 0 ? 'primary' : 'secondary'}>
                                    {slotTime(minute)}
                                </Text>
                            </TableCell>
                            {load.courts.map((court, i) => {
                                const cell = columns[i].get(minute)
                                // The hold above spans this row.
                                if (cell && cell.covered) return null
                                const half = cell ? cell.half : null
                                return (
                                    <SlotCell
                                        key={court.court_id}
                                        court={court}
                                        slot={half}
                                        span={cell ? cell.span : 1}
                                        playerSlot={cell ? cell.slot : null}
                                        isSelected={!!half && selection.has(cellKey(court.court_id, half.start_time))}
                                        isOpen={!!half && !!half.booking_id && half.booking_id === openBooking}
                                        onToggle={half ? toggle(half) : undefined}
                                        onOpen={setOpenBooking}
                                    />
                                )
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        )
    }

    const content = (
        <LayoutContent padding={6}>
            <Stack gap={4}>
                {navigation}
                {notice && (
                    <Banner
                        status={notice.status}
                        title={notice.title}
                        isDismissable
                        dismissLabel="Masquer le message"
                        onDismiss={() => setNotice(null)}
                    />
                )}
                {load.courts.length > 0 && !load.isClosed && selectionBar}
                {body}
            </Stack>
        </LayoutContent>
    )

    const panel = openBooking && (
        <LayoutPanel width={PANEL_WIDTH} hasDivider padding={0} label="Détail du créneau">
            <CourtBookingPanel
                key={openBooking}
                clubId={clubId}
                bookingId={openBooking}
                onClose={() => setOpenBooking('')}
                onChanged={changed}
                onUpdated={updated}
                onConflict={conflict}
                onSessionLost={onSessionLost}
            />
        </LayoutPanel>
    )

    return (
        <>
            <Layout height="auto" content={content} end={panel} />
            <BlockDialog
                isOpen={isBlockOpen}
                clubId={clubId}
                date={date}
                cells={[...selection.values()]}
                onClose={() => setBlockOpen(false)}
                onBlocked={onBlocked}
                onFailure={status => {
                    setBlockOpen(false)
                    fail(status)
                }}
                onSessionLost={onSessionLost}
            />
            <CreateGameDialog
                isOpen={isGameOpen && !!gameCell}
                clubId={clubId}
                date={date}
                cell={gameCell}
                where={gameWhere}
                onClose={() => setGameOpen(false)}
                onCreated={title => {
                    setGameOpen(false)
                    changed('success', title)
                }}
                onFailure={(status, title) => {
                    setGameOpen(false)
                    if (status === 409) changed('warning', title)
                    else setNotice({ status: 'error', title })
                }}
                onSessionLost={onSessionLost}
            />
        </>
    )
}

CourtDay.propTypes = {
    clubId: PropTypes.string.isRequired,
    date: PropTypes.shape({
        year: PropTypes.number.isRequired,
        month: PropTypes.number.isRequired,
        day: PropTypes.number.isRequired,
    }).isRequired,
    onDateChange: PropTypes.func.isRequired,
    // Switches to the opening hours, from a day the club is closed.
    onOpenHours: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}

function capitalize(text) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

// "Terrain 1 · Central — samedi 24 octobre à 18:30", for the picked cell.
function cellWhere(courts, cell, date) {
    const court = courts.find(c => c.court_id === cell.court_id)
    const slot = court && court.slots.find(s => s.start_time === cell.start_time)
    const when = slot ? ` à ${slotTime(slot.start_minute)}` : ''
    return `${court ? courtTitle(court) : 'Terrain'} — ${dayTitle(date)}${when}`
}

// A free player's slot starts on that court at that half-hour.
function startsAPlayerSlot(courts, cell) {
    const court = courts.find(c => c.court_id === cell.court_id)
    return !!court && court.slots.some(s => s.start_time === cell.start_time && s.state === 'CONSOLE_SLOT_STATE_FREE')
}

// One court at one half-hour, or one hold over several. Free and still to
// come: picked and unpicked by a click, lost ones included - blocking a
// half-hour nobody can book costs nothing. Held by something: opens it in the
// panel. Over and free, or no half-hour at that time on that court: nothing
// to do.
function SlotCell({ court, slot, span, playerSlot, isSelected, isOpen, onToggle, onOpen }) {
    if (!slot) {
        return (
            <TableCell>
                <Text color="disabled">—</Text>
            </TableCell>
        )
    }

    const look = halfHourLook(slot)
    const isFree = slot.state === 'CONSOLE_SLOT_STATE_FREE'
    const canPick = isFree && !slot.is_past
    const canOpen = !isFree && !!slot.booking_id
    const where = `${courtTitle(court)}, ${slotTime(slot.start_minute)}`

    let style
    if (isSelected) style = cellStyles.selected
    else if (isOpen) style = cellStyles.open
    else if (slot.is_past) style = canOpen ? { ...cellStyles.past, ...cellStyles.interactive } : cellStyles.past
    else if (look.isLost) style = cellStyles.lost
    else if (canPick || canOpen) style = cellStyles.interactive
    // Where a player's slot starts, a rule above it: the 1h30 slots move with
    // the blocks, and this is how the admin sees where they fall now.
    if (playerSlot) style = { ...style, ...cellStyles.slotStart }

    const act = () => {
        if (canPick) onToggle(court)
        else if (canOpen) onOpen(slot.booking_id)
    }
    const interactive = canPick || canOpen
        ? {
            role: 'button',
            tabIndex: 0,
            'aria-label': `${where} : ${isSelected ? 'sélectionné' : look.text}${look.isVisible ? ', motif visible par les adhérents' : ''}`,
            'aria-pressed': canPick ? isSelected : undefined,
            onClick: act,
            onKeyDown: event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault()
                    act()
                }
            },
        }
        : {}

    return (
        <TableCell style={style} rowSpan={span > 1 ? span : undefined} {...interactive}>
            <SlotContent
                look={look}
                isSelected={isSelected}
                isPast={slot.is_past}
                range={playerSlot ? slotRangeText(playerSlot) : (span > 1 ? slotRangeText({ ...slot, duration_minutes: span * 30 }) : '')}
            />
        </TableCell>
    )
}

SlotCell.propTypes = {
    court: PropTypes.object.isRequired,
    slot: PropTypes.object,
    // How many half-hours the cell covers: a hold is one cell.
    span: PropTypes.number,
    // The player's slot starting at this half-hour, when one does.
    playerSlot: PropTypes.object,
    isSelected: PropTypes.bool,
    isOpen: PropTypes.bool,
    onToggle: PropTypes.func,
    onOpen: PropTypes.func.isRequired,
}

function SlotContent({ look, isSelected, isPast, range }) {
    if (isSelected) {
        return (
            <Stack direction="horizontal" gap={1.5} vAlign="center">
                <Icon icon="check" color="accent" size="sm" />
                <Text color="accent" weight="semibold" size="sm">Sélectionné</Text>
            </Stack>
        )
    }
    const color = isPast ? 'secondary' : 'primary'
    if (look.token) {
        // The block's label beside its token, not under it, so every row of
        // the grid keeps the same height whatever its cells hold.
        return (
            <Stack direction="horizontal" gap={2} vAlign="center">
                <StackItem size="static">
                    <Token label={look.text} color={look.token} size="sm" isDisabled={isPast} />
                </StackItem>
                {(look.detail || range) && (
                    <StackItem size="fill">
                        <Text size="sm" color="secondary" maxLines={1}>
                            {[range, look.detail].filter(Boolean).join(' · ')}
                        </Text>
                    </StackItem>
                )}
                {look.isVisible && (
                    // The club's members see this block's reason in the app.
                    <StackItem size="static">
                        <Icon icon={EyeIcon} size="sm" color="secondary" label="Motif visible par les adhérents" />
                    </StackItem>
                )}
            </Stack>
        )
    }
    return (
        <Stack direction="horizontal" gap={1.5} vAlign="center">
            <StatusDot variant={look.dot} label={look.text} />
            <Text size="sm" color={look.isLost ? 'secondary' : color} maxLines={1}>{look.text}</Text>
            {range && <Text size="sm" color="secondary" hasTabularNumbers maxLines={1}>{range}</Text>}
        </Stack>
    )
}

SlotContent.propTypes = {
    look: PropTypes.object.isRequired,
    isSelected: PropTypes.bool,
    isPast: PropTypes.bool,
    // "10:30 – 12:00": the player's slot starting here, or the hold's span.
    range: PropTypes.string,
}

// "Bloquer…": why, and an optional label, for every slot picked. Slots
// somebody took meanwhile are skipped by the server and counted in the
// message it leaves.
function BlockDialog({ isOpen, clubId, date, cells, onClose, onBlocked, onFailure, onSessionLost }) {
    const [kind, setKind] = useState('')
    const [assignees, setAssignees] = useState([])
    const [label, setLabel] = useState('')
    const [showReason, setShowReason] = useState(false)
    const [isBlocking, setBlocking] = useState(false)

    // Every "Bloquer…" starts private: a label written for the staff is not
    // made public by a choice left over from the previous block.
    useEffect(() => {
        if (isOpen) {
            setKind('')
            setLabel('')
            setShowReason(false)
            setAssignees([])
        }
    }, [isOpen])

    const confirm = async () => {
        if (!kind) return
        setBlocking(true)
        let answer
        try {
            answer = await blockCourtSlots(clubId, date, kind, label.trim(), cells, showReason, assignees.map(a => a.club_user_id))
        } catch (e) {
            answer = { status: 0 }
        } finally {
            setBlocking(false)
        }
        if (answer.status === 200 && answer.json) {
            onBlocked((answer.json.blocked || []).length, answer.json.skipped || [])
        } else {
            onFailure(answer.status)
        }
    }

    return (
        <Dialog isOpen={isOpen} onOpenChange={open => !open && onClose()} purpose="form" width={480}>
            <Layout
                header={
                    <DialogHeader
                        title={`Bloquer ${selectionText(cells.length).replace(/ sélectionnées?$/, '')}`}
                        subtitle={capitalize(dayTitle(date))}
                        onOpenChange={() => onClose()}
                    />
                }
                content={
                    <LayoutContent>
                        <Stack gap={4}>
                            <RadioList label="Raison" isRequired value={kind} onChange={setKind} orientation="horizontal">
                                {BLOCK_KINDS.map(k => (
                                    <RadioListItem key={k.value} value={k.value} label={k.label} />
                                ))}
                            </RadioList>
                            <TextInput
                                label="Libellé"
                                isOptional
                                value={label}
                                onChange={value => setLabel(value.slice(0, LABEL_MAX))}
                                placeholder="ex. Tournoi P250 interne"
                                description={`Affiché sur la grille. ${LABEL_MAX} caractères au plus.`}
                            />
                            {/* A checkbox, not a switch: nothing happens until
                                "Bloquer" is pressed. */}
                            <CheckboxInput
                                label={SHOW_REASON_LABEL}
                                value={showReason}
                                onChange={setShowReason}
                                description={showReasonDescription(kind, label, showReason)}
                            />
                            <AssigneePicker
                                clubId={clubId}
                                value={assignees}
                                onChange={setAssignees}
                                onSessionLost={onSessionLost}
                            />
                        </Stack>
                    </LayoutContent>
                }
                footer={
                    <LayoutFooter>
                        <Stack direction="horizontal" gap={2} hAlign="end">
                            <Button label="Annuler" variant="secondary" onClick={onClose} />
                            <Button
                                label="Bloquer"
                                variant="primary"
                                isDisabled={!kind || cells.length === 0}
                                isLoading={isBlocking}
                                onClick={confirm}
                            />
                        </Stack>
                    </LayoutFooter>
                }
            />
        </Dialog>
    )
}

BlockDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    clubId: PropTypes.string.isRequired,
    date: PropTypes.object.isRequired,
    cells: PropTypes.arrayOf(PropTypes.shape({
        court_id: PropTypes.string.isRequired,
        start_time: PropTypes.string.isRequired,
    })).isRequired,
    onClose: PropTypes.func.isRequired,
    onBlocked: PropTypes.func.isRequired,
    onFailure: PropTypes.func.isRequired,
    onSessionLost: PropTypes.func.isRequired,
}
